import { memo, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Alert from "@mui/material/Alert";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useDelete, usePost, usePut } from "../../actprep-ui/hooks/api";
import scheduleItemTypeOptions from "./scheduleItemTypeOptions";
import { scheduleItemTypes } from "../../actprep-ui/constants/program";
import { FormControlLabel, FormHelperText, Switch } from "@mui/material";

const ProgramDayScheduleItemForm = memo(function ProgramDayScheduleItemForm({
  order,
  scheduleItem,
  onSave,
  onDelete,
  onChange,
  programDay,
  edited,
}) {
  const editing = Boolean(scheduleItem?.uuid);
  const [error, setError] = useState();
  const createScheduleItemMutation = usePost(apiUrls.admin.scheduleItems());
  const editScheduleItemMutation = usePut(apiUrls.admin.scheduleItems());
  const deleteScheduleItemMutation = useDelete(apiUrls.admin.scheduleItems());
  const scheduleItemDefaultFormData = useMemo(
    () => ({
      label: scheduleItem.label || "",
      description: scheduleItem.description || "",
      required: scheduleItem.required || false,
      item_type: scheduleItem.item_type || scheduleItemTypes.BASIC,
      item_metadata: scheduleItem.item_metadata || {},
    }),
    [scheduleItem]
  );

  const [formData, setFormData] = useState(scheduleItemDefaultFormData);

  const { ExtraFields } = scheduleItemTypeOptions[formData.item_type];

  const handleChange = (name) => (event) => {
    if (onChange) {
      onChange();
    }
    setFormData({
      ...formData,
      [name]: event.target.value,
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    let response;
    if (editing) {
      response = await editScheduleItemMutation.mutateAsync({
        ...formData,
        order: order,
        program_day_uuid: programDay.uuid,
        schedule_item_uuid: scheduleItem.uuid,
      });
    } else {
      response = await createScheduleItemMutation.mutateAsync({
        ...formData,
        order: order,
        program_day_uuid: programDay.uuid,
      });
    }
    const { data } = response;
    if (!data?.success) {
      setError(data?.reason);
      return;
    }
    if (error) {
      setError(null);
    }

    if (editing) {
      showSuccessSnackbar("Schedule Item updated!");
    } else {
      showSuccessSnackbar("Schedule Item created!");
    }
    if (onSave) {
      onSave(data.schedule_item);
    }
  };

  const handleDelete = async () => {
    if (!editing) {
      onDelete(scheduleItem);
      return;
    }

    const response = await deleteScheduleItemMutation.mutateAsync({
      schedule_item_uuid: scheduleItem.uuid,
    });
    const { data } = response;
    if (data?.success && onDelete) {
      onDelete(scheduleItem);
    }
  };

  useEffect(() => {
    setFormData(scheduleItemDefaultFormData);
  }, [scheduleItemDefaultFormData]);

  return (
    <Box
      component="form"
      onSubmit={handleSave}
      onClick={(event) => event.stopPropagation()}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label="Label"
            variant="standard"
            value={formData.label}
            onChange={handleChange("label")}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            label="Description"
            variant="standard"
            value={formData.description}
            onChange={handleChange("description")}
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            sx={{ m: 0 }}
            label="Required"
            control={
              <Switch
                size="small"
                color="secondary"
                checked={formData.required}
                onChange={(_, checked) => {
                  if (onChange) {
                    onChange();
                  }
                  setFormData((curr) => ({ ...curr, required: checked }));
                }}
              />
            }
          />
          <FormHelperText>
            Employees only: Required items will transfer to the to-do list if
            not completed.
          </FormHelperText>
        </Grid>
        {ExtraFields && (
          <ExtraFields
            formData={formData}
            setFormData={(newFormData) => {
              if (onChange) {
                onChange();
              }
              setFormData(newFormData);
            }}
          />
        )}
        <Grid size={12}>
          <ButtonGroup variant="outlined">
            <Button type="submit" disabled={!edited}>
              Save
            </Button>
            {onDelete && <Button onClick={handleDelete}>Remove</Button>}
          </ButtonGroup>
        </Grid>
        {error && (
          <Grid size={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});
export default ProgramDayScheduleItemForm;
